<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import dayjs from "dayjs";
import apiClient from "@/apiClient.js";
import { useListStoreNew } from "@/stores/ListStoreNew";
import { usePagination } from "@/composables/usePagination";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import { useAuthStore } from "@/stores/AuthStore";
import { ArrowUpTrayIcon, PlusCircleIcon } from "@heroicons/vue/24/solid";
import FuelCardUsageChart from "@/components/FuelCardUsageChart.vue";

const list = useListStoreNew();
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

const searchTerm = ref(route.query?.search ?? "");
const perPageOptions = [25, 50, 100];
const initialPerPage = Number(route.query?.per_page);
const perPage = ref(
  perPageOptions.includes(initialPerPage) ? initialPerPage : 25,
);
const canImportHistoricReports = computed(
  () => Boolean(auth.user?.is_admin) || auth.hasRole("super"),
);

const showCreateModal = ref(false);
const createSaving = ref(false);
const createError = ref("");
const createForm = ref({
  card_name: "",
  card_number: "",
  expiry_date: "",
  active: true,
});
const importFiles = ref([]);
const importSaving = ref(false);
const importError = ref("");
const importResult = ref(null);
const quickSearchTerm = ref("");
const quickSearchResults = ref([]);
const quickSearchLoading = ref(false);
const quickSearchError = ref("");
const usageOverview = ref({
  labels: [],
  quantities: [],
  amounts: [],
  total_transactions: 0,
  total_quantity: 0,
  total_amount: 0,
});
const usageOverviewLoading = ref(false);
const usageOverviewError = ref("");
let quickSearchTimer = null;
let quickSearchRequestId = 0;

const { setUrl, changePage } = usePagination(list, null, () => ({
  search: searchTerm.value || undefined,
  per_page: perPage.value,
}));

const applyFilters = () => {
  list.clearFilters();
  if (searchTerm.value?.trim()) {
    list.addFilter("search", encodeURIComponent(searchTerm.value.trim()));
  }
  list.addFilter("per_page", perPage.value);
};

const runSearch = () => {
  applyFilters();
  list.resetPage();
  setUrl();
};

const resetFilters = () => {
  searchTerm.value = "";
  perPage.value = 25;
  runSearch();
};

const viewFuelCard = (card, event = null) => {
  if (
    event &&
    (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey)
  ) {
    return;
  }

  router.push({
    name: "fuel-cards-view",
    params: { id: card.id },
  });
};

const openNewTab = (event, card) => {
  if (event.button === 1) {
    window.open(`/fuel-cards/${card.id}`, "_blank");
  }
};

const formatDate = (value) =>
  value ? dayjs(value).format("DD MMM YYYY") : "—";
const formatQuantity = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(2)} L`;
const formatMoney = (value) =>
  value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
      }).format(Number(value));

const selectedImportNames = computed(() =>
  importFiles.value.map((file) => file?.name).filter(Boolean),
);

const isQuickSearchActive = computed(
  () => quickSearchTerm.value.trim().length > 0,
);
const displayedCards = computed(() =>
  isQuickSearchActive.value ? quickSearchResults.value : list.getIndex,
);

const quickSearchSummary = computed(() => {
  if (!isQuickSearchActive.value) {
    return "";
  }

  const count = quickSearchResults.value.length;
  return `Showing ${count} matching fuel card${count === 1 ? "" : "s"} across all cards.`;
});

const openCreateModal = () => {
  createError.value = "";
  createForm.value = {
    card_name: "",
    card_number: "",
    expiry_date: "",
    active: true,
  };
  showCreateModal.value = true;
};

const closeCreateModal = () => {
  if (createSaving.value) {
    return;
  }
  showCreateModal.value = false;
};

const createFuelCard = async () => {
  const cardName = createForm.value.card_name?.trim();
  const cardNumber = createForm.value.card_number?.trim();
  const expiryDate = createForm.value.expiry_date;
  if (!cardName) {
    createError.value = "Card name is required.";
    return;
  }
  if (!cardNumber) {
    createError.value = "Card number is required.";
    return;
  }
  if (!expiryDate) {
    createError.value = "Expiry date is required.";
    return;
  }

  createSaving.value = true;
  createError.value = "";
  try {
    const { data } = await apiClient.post("/api/fuel-card", {
      card_name: cardName,
      card_number: cardNumber,
      expiry_date: expiryDate,
      active: createForm.value.active,
    });
    showCreateModal.value = false;
    runSearch();
    if (data?.id) {
      router.push({
        name: "fuel-cards-view",
        params: { id: data.id },
      });
    }
  } catch (error) {
    createError.value =
      error?.response?.data?.message ?? "Unable to create fuel card.";
  } finally {
    createSaving.value = false;
  }
};

const onImportFilesChange = (event) => {
  importFiles.value = [...(event?.target?.files || [])];
};

const importHistoricReports = async () => {
  if (!importFiles.value.length) {
    importError.value = "Select one or more XLSX statements to import.";
    return;
  }

  importSaving.value = true;
  importError.value = "";
  importResult.value = null;

  const formData = new FormData();
  importFiles.value.forEach((file, index) => {
    formData.append(`files[${index}]`, file);
  });

  try {
    const { data } = await apiClient.post(
      "/api/fuel-card/import-history",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    importResult.value = data;
    importFiles.value = [];
    runSearch();
  } catch (error) {
    importError.value =
      error?.response?.data?.message ??
      "Unable to import historic fuel reports.";
  } finally {
    importSaving.value = false;
  }
};

const fetchQuickSearchResults = async (term) => {
  const normalized = term.trim();
  const requestId = ++quickSearchRequestId;

  if (!normalized) {
    quickSearchResults.value = [];
    quickSearchError.value = "";
    quickSearchLoading.value = false;
    return;
  }

  quickSearchLoading.value = true;
  quickSearchError.value = "";

  try {
    const { data } = await apiClient.get("/api/fuel-card", {
      params: {
        page: "all",
        search: normalized,
        search_scope: "identity",
      },
    });

    if (requestId !== quickSearchRequestId) {
      return;
    }

    quickSearchResults.value = Array.isArray(data) ? data : [];
  } catch (error) {
    if (requestId !== quickSearchRequestId) {
      return;
    }

    quickSearchResults.value = [];
    quickSearchError.value =
      error?.response?.data?.message ?? "Unable to run quick search.";
  } finally {
    if (requestId === quickSearchRequestId) {
      quickSearchLoading.value = false;
    }
  }
};

const loadUsageOverview = async () => {
  usageOverviewLoading.value = true;
  usageOverviewError.value = "";

  try {
    const { data } = await apiClient.get("/api/fuel-card/usage-summary");
    usageOverview.value = {
      labels: Array.isArray(data?.labels) ? data.labels : [],
      quantities: Array.isArray(data?.quantities) ? data.quantities : [],
      amounts: Array.isArray(data?.amounts) ? data.amounts : [],
      total_transactions: Number(data?.total_transactions ?? 0),
      total_quantity: Number(data?.total_quantity ?? 0),
      total_amount: Number(data?.total_amount ?? 0),
    };
  } catch (error) {
    usageOverviewError.value =
      error?.response?.data?.message ?? "Unable to load fuel usage overview.";
  } finally {
    usageOverviewLoading.value = false;
  }
};

watch(quickSearchTerm, (value) => {
  if (quickSearchTimer) {
    clearTimeout(quickSearchTimer);
  }

  if (!value.trim()) {
    quickSearchRequestId++;
    quickSearchResults.value = [];
    quickSearchError.value = "";
    quickSearchLoading.value = false;
    return;
  }

  quickSearchTimer = setTimeout(() => {
    fetchQuickSearchResults(value);
  }, 200);
});

onMounted(() => {
  list.configure({ remote: "/api/fuel-card", local: "/fuel-cards" });
  applyFilters();
  list.fetchIndex();
  loadUsageOverview();
});

onBeforeUnmount(() => {
  if (quickSearchTimer) {
    clearTimeout(quickSearchTimer);
  }
});
</script>

<template>
  <div class="space-y-4">
    <div class="box">
      <div class="flex flex-col gap-3 lg:flex-row lg:items-center">
        <input
          v-model="searchTerm"
          type="text"
          class="input flex-1"
          placeholder="Search name, number, vehicle, or staff"
          @keyup.enter="runSearch"
        />
        <select
          v-model.number="perPage"
          class="input lg:w-36"
          @change="runSearch"
        >
          <option
            v-for="option in perPageOptions"
            :key="option"
            :value="option"
          >
            {{ option }} / page
          </option>
        </select>
        <button class="btn accent" @click.prevent="runSearch">Search</button>
        <button class="btn" @click.prevent="resetFilters">Clear</button>
        <button
          class="btn accent flex items-center gap-2 whitespace-nowrap"
          @click.prevent="openCreateModal"
        >
          <PlusCircleIcon class="icon" />
          <span>New Fuel Card</span>
        </button>
      </div>
    </div>

    <section v-if="canImportHistoricReports" class="box space-y-4">
      <div
        class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
      >
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">
            Historic Fuel Import
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400">
            Upload historic Allstar fuel drawing spreadsheets. Missing fuel
            cards will be created automatically and non-fuel rows like tolls or
            fees are ignored.
          </p>
        </div>
        <button
          class="btn accent flex items-center gap-2 whitespace-nowrap"
          :disabled="importSaving"
          @click.prevent="importHistoricReports"
        >
          <ArrowUpTrayIcon class="icon" />
          <span>{{ importSaving ? "Importing..." : "Import Statements" }}</span>
        </button>
      </div>

      <label class="block">
        <span class="text-sm font-medium text-slate-600 dark:text-slate-300"
          >Statement Files</span
        >
        <input
          type="file"
          multiple
          accept=".xlsx,.xls"
          class="mt-1 block w-full rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-sm text-slate-700 shadow-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          @change="onImportFilesChange"
        />
      </label>

      <div
        v-if="selectedImportNames.length"
        class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/30 dark:text-slate-200"
      >
        <div class="font-medium">
          {{ selectedImportNames.length }} file{{
            selectedImportNames.length === 1 ? "" : "s"
          }}
          selected
        </div>
        <div class="mt-2 flex flex-wrap gap-2">
          <span
            v-for="name in selectedImportNames"
            :key="name"
            class="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700"
          >
            {{ name }}
          </span>
        </div>
      </div>

      <p
        v-if="importError"
        class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
      >
        {{ importError }}
      </p>

      <div
        v-if="importResult"
        class="space-y-4 rounded-2xl border border-cyan-200 bg-cyan-50/70 p-4 dark:border-cyan-900/40 dark:bg-cyan-900/10"
      >
        <div class="grid gap-3 md:grid-cols-5">
          <div
            class="rounded-xl bg-white/90 p-3 shadow-sm dark:bg-slate-900/40"
          >
            <div class="text-xs uppercase tracking-wide text-slate-500">
              Files
            </div>
            <div
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ importResult.summary?.files_processed || 0 }}
            </div>
          </div>
          <div
            class="rounded-xl bg-white/90 p-3 shadow-sm dark:bg-slate-900/40"
          >
            <div class="text-xs uppercase tracking-wide text-slate-500">
              Rows Imported
            </div>
            <div
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ importResult.summary?.created_rows || 0 }}
            </div>
          </div>
          <div
            class="rounded-xl bg-white/90 p-3 shadow-sm dark:bg-slate-900/40"
          >
            <div class="text-xs uppercase tracking-wide text-slate-500">
              Duplicates
            </div>
            <div
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ importResult.summary?.duplicate_rows || 0 }}
            </div>
          </div>
          <div
            class="rounded-xl bg-white/90 p-3 shadow-sm dark:bg-slate-900/40"
          >
            <div class="text-xs uppercase tracking-wide text-slate-500">
              Skipped
            </div>
            <div
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ importResult.summary?.skipped_rows || 0 }}
            </div>
          </div>
          <div
            class="rounded-xl bg-white/90 p-3 shadow-sm dark:bg-slate-900/40"
          >
            <div class="text-xs uppercase tracking-wide text-slate-500">
              Cards Created
            </div>
            <div
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ importResult.summary?.created_cards || 0 }}
            </div>
          </div>
        </div>

        <div class="space-y-2">
          <div
            v-for="file in importResult.files || []"
            :key="file.filename"
            class="rounded-xl border px-4 py-3 text-sm dark:border-slate-700"
            :class="
              file.status === 'failed'
                ? 'border-red-200 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                : 'border-emerald-200 bg-white/90 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200'
            "
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div class="font-semibold">{{ file.filename }}</div>
              <span class="text-xs uppercase tracking-wide">{{
                file.status
              }}</span>
            </div>
            <p v-if="file.status === 'failed'" class="mt-1">{{ file.error }}</p>
            <p v-else class="mt-1">{{ file.summary }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="box space-y-4">
      <div
        class="flex flex-col gap-2 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">
            Fleet Fuel Usage
          </h2>
          <p class="text-sm text-slate-500 dark:text-slate-400">
            All fuel cards combined over the last 12 months.
          </p>
        </div>
        <div class="grid gap-3 sm:grid-cols-3">
          <div
            class="rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900/30"
          >
            <div class="text-xs uppercase tracking-wide text-slate-500">
              Transactions
            </div>
            <div
              class="mt-1 text-xl font-semibold text-slate-900 dark:text-white"
            >
              {{ usageOverview.total_transactions }}
            </div>
          </div>
          <div
            class="rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900/30"
          >
            <div class="text-xs uppercase tracking-wide text-slate-500">
              Total Volume
            </div>
            <div
              class="mt-1 text-xl font-semibold text-slate-900 dark:text-white"
            >
              {{ formatQuantity(usageOverview.total_quantity) }}
            </div>
          </div>
          <div
            class="rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900/30"
          >
            <div class="text-xs uppercase tracking-wide text-slate-500">
              Total Spend
            </div>
            <div
              class="mt-1 text-xl font-semibold text-slate-900 dark:text-white"
            >
              {{ formatMoney(usageOverview.total_amount) }}
            </div>
          </div>
        </div>
      </div>

      <p
        v-if="usageOverviewError"
        class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
      >
        {{ usageOverviewError }}
      </p>

      <div
        v-else-if="usageOverviewLoading"
        class="h-72 animate-pulse rounded-2xl border border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800/60"
      ></div>

      <FuelCardUsageChart
        v-else
        :labels="usageOverview.labels"
        :quantities="usageOverview.quantities"
        :amounts="usageOverview.amounts"
      />
    </section>

    <section class="box overflow-hidden p-0">
      <div
        class="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 md:flex-row md:items-center md:justify-between dark:border-slate-700"
      >
        <div>
          <h2 class="text-sm font-semibold text-slate-900 dark:text-white">
            Fuel Cards
          </h2>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            {{
              quickSearchLoading
                ? "Searching all fuel cards..."
                : isQuickSearchActive
                  ? quickSearchSummary
                  : "Quick search checks every card name and card number."
            }}
          </p>
        </div>
        <input
          v-model="quickSearchTerm"
          type="text"
          class="input w-full md:max-w-xs"
          placeholder="Quick search all cards"
        />
      </div>

      <p
        v-if="quickSearchError"
        class="border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
      >
        {{ quickSearchError }}
      </p>

      <table class="list w-full" v-if="!list.isLoading && !quickSearchLoading">
        <thead class="hidden md:table-header-group">
          <tr>
            <th class="px-4 py-2 text-left">Card Name</th>
            <th class="px-4 py-2 text-left">Card Number</th>
            <th class="px-4 py-2 text-left">Expiry</th>
            <th class="px-4 py-2 text-left">Current Vehicle</th>
            <th class="px-4 py-2 text-left">Assigned Staff</th>
            <th class="px-4 py-2 text-left w-28">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="card in displayedCards"
            :key="card.id"
            class="cursor-pointer border-b border-gray-200 hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-900"
            @click="(event) => viewFuelCard(card, event)"
            @auxclick="(event) => openNewTab(event, card)"
          >
            <td
              class="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100"
            >
              {{ card.card_name || "—" }}
              <div class="text-xs text-slate-500 md:hidden">
                {{ card.card_number || "—" }} ·
                {{ formatDate(card.expiry_date) }}
              </div>
            </td>
            <td
              class="hidden px-4 py-3 font-semibold text-slate-900 dark:text-slate-100 md:table-cell"
            >
              {{ card.card_number }}
            </td>
            <td class="hidden px-4 py-3 md:table-cell">
              {{ formatDate(card.expiry_date) }}
            </td>
            <td class="hidden px-4 py-3 md:table-cell">
              {{ card.current_vehicle?.registrationNumber || "Unassigned" }}
            </td>
            <td class="hidden px-4 py-3 md:table-cell">
              {{ card.assigned_staff?.contact?.name || "—" }}
            </td>
            <td class="px-4 py-3">
              <span
                :class="[
                  'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold',
                  card.active
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-rose-100 text-rose-700',
                ]"
              >
                {{ card.active ? "Active" : "Inactive" }}
              </span>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="6" class="px-4 py-4" v-if="displayedCards.length < 1">
              No fuel cards to display.
            </td>
          </tr>
          <tr v-if="!isQuickSearchActive">
            <th colspan="6" class="px-4 py-4">
              <PrevNextButtons
                :showPrev="list.showPrev"
                :showNext="list.showNext"
                @prev="changePage(-1)"
                @next="changePage(1)"
              />
            </th>
          </tr>
        </tfoot>
      </table>

      <table class="list w-full" v-else>
        <tbody>
          <tr
            v-for="n in 12"
            :key="n"
            class="animate-pulse border-b border-gray-300"
          >
            <td class="p-3">
              <div class="mb-2 h-4 w-32 rounded bg-slate-200"></div>
              <div class="h-3 w-40 rounded bg-slate-200 md:hidden"></div>
            </td>
            <td class="hidden p-3 md:table-cell">
              <div class="h-4 w-24 rounded bg-slate-200"></div>
            </td>
            <td class="hidden p-3 md:table-cell">
              <div class="h-4 w-28 rounded bg-slate-200"></div>
            </td>
            <td class="hidden p-3 md:table-cell">
              <div class="h-4 w-28 rounded bg-slate-200"></div>
            </td>
            <td class="hidden p-3 md:table-cell">
              <div class="h-4 w-36 rounded bg-slate-200"></div>
            </td>
            <td class="p-3">
              <div class="h-4 w-16 rounded bg-slate-200"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <div
      v-if="showCreateModal"
      class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 p-4"
    >
      <div
        class="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
      >
        <div
          class="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700"
        >
          <div>
            <p
              class="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              Fleet
            </p>
            <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
              Create Fuel Card
            </h2>
          </div>
          <button
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            @click.prevent="closeCreateModal"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div class="space-y-4 p-6">
          <div class="grid gap-4 md:grid-cols-2">
            <label class="block">
              <span
                class="text-sm font-medium text-slate-600 dark:text-slate-300"
                >Card Name</span
              >
              <input
                v-model="createForm.card_name"
                type="text"
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                placeholder="e.g. Main Fleet Card"
                autofocus
              />
            </label>

            <label class="block">
              <span
                class="text-sm font-medium text-slate-600 dark:text-slate-300"
                >Card Number</span
              >
              <input
                v-model="createForm.card_number"
                type="text"
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm uppercase text-slate-900 shadow-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                placeholder="e.g. FC-001"
              />
            </label>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="block">
              <span
                class="text-sm font-medium text-slate-600 dark:text-slate-300"
                >Expiry Date</span
              >
              <input
                v-model="createForm.expiry_date"
                type="date"
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 [color-scheme:light] dark:[color-scheme:dark] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              />
            </label>

            <label class="flex items-end">
              <span
                class="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200"
              >
                <input v-model="createForm.active" type="checkbox" />
                <span>Active</span>
              </span>
            </label>
          </div>

          <p
            v-if="createError"
            class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          >
            {{ createError }}
          </p>

          <div
            class="flex items-center justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-700"
          >
            <button
              class="btn"
              :disabled="createSaving"
              @click.prevent="closeCreateModal"
            >
              Cancel
            </button>
            <button
              class="btn accent"
              :disabled="createSaving"
              @click.prevent="createFuelCard"
            >
              {{ createSaving ? "Saving..." : "Create Fuel Card" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
